import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DeleteIcon } from '../assets/DeleteIcon.jsx'
import PageShell from '../layouts/PageShell';
import AppNavbar from '../features/AppNavbar';
import PipelineTabs from '../features/pipeline/PipelineTabs.jsx';
import AddOpportunity from '../features/pipeline/AddOpportunity/AddOpportunity.jsx';
import ConfirmModal from '../components/ConfirmModal.jsx';
import ManageTagsModal from '../features/pipeline/tags/ManageTagsModal.jsx';
import { SYSTEM_TAGS } from '../features/pipeline/tags/tagsConfig.js';
import { getStageFromStatus, getStageCounts } from "../features/pipeline/utils.js";
import { getPipelineColumns } from '../features/pipeline/grid/columns.js'
import PipelineGrid, { PIPELINE_PAGE_SIZES } from '../features/pipeline/grid/PipelineGrid.jsx';
import PipelinePagination from '../features/pipeline/pagination/PipelinePagination.jsx';
import { reilmAPI } from '../services/apiClient';
import { toFetchError } from '../features/pipeline/fetchError.js';
import { useAuth } from '../features/auth/AuthContext.jsx';
import { getIn } from './getIn';

// frame `popup-delete-2`: 360 x 232; padTop/titleGap from the baselines, as the flip popups' frames.
const DELETE_PROPERTIES_FRAME = { minHeight: 232, padX: 30, padTop: 49, padBottom: 40, titleSize: 18, titleLeading: 22, titleGap: 9, buttonWidth: 132 };

const Pipeline = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [selectedRows, setSelectedRows] = useState([])
    const [properties, setProperties] = useState([]);
    const [customTags, setCustomTags] = useState([]);
    const [activeStage, setActiveStage] = useState("all");
    // Not persisted: a reload starts at 25 / page (spec.md d3).
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PIPELINE_PAGE_SIZES[0] });
    const gridTopRef = useRef(null);
    // Set by a user page/size change; the scroll waits for the new page to render (see effect below).
    const scrollAfterPageRef = useRef(false);
    const [addExpanded, setAddExpanded] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showManageTags, setShowManageTags] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    // Shared banner across every row-level save on this screen (pin, stage, tags), same shape as
    // Arv.jsx/PropertyDashboard.jsx's saveError -- src/pages/README.md, "Background-save race".
    const [saveError, setSaveError] = useState(null);
    // Which call site currently owns the banner, keyed per row and per field so an unrelated row or
    // field's settlement cannot clear or raise this one's message. Entries keyed to a row are
    // dropped on that row's own deletion (handleDelete) -- a hard-deleted row can never settle
    // again, so nothing else would ever clear one left keyed to it.
    const failedSavesRef = useRef(new Set());
    // Full keys (`stage:p1`, not just the row id) hard-deleted this session -- fail-closed tombstone
    // consulted by markSaveOk/markSaveFailed below. src/pages/README.md's Pipeline.jsx section covers
    // why `stage:` specifically needs this (f-8 round 2, finding 2).
    const deletedKeysRef = useRef(new Set());
    // Per-key generation counter for pin:/tags: -- src/pages/README.md, "Background-save race".
    // stage: opts out; that file's own Pipeline.jsx section covers why and what covers it instead.
    const saveGenerationsRef = useRef(new Map());
    // Last value the client SENT for a pin toggle; among settled responses the last to arrive is the
    // best estimate of current server state. Per row and lazily seeded -- Arv.jsx's
    // confirmedArchivedRef shape, not PropertyDashboard's single-key one, since this grid's rows
    // come and go across fetches and stage-tab filters rather than being one fixed property.
    const confirmedPinRef = useRef(new Map());
    // The tag array the client is currently building toward for a row -- what handleTagToggle reads
    // as its base instead of `properties` state, so a second toggle fired before the first settles
    // still sees the first's own change (tags/README.md, "Lost-update race"). Corrected in place by
    // handleTagDeleted when a tag is removed globally while a row's own toggle is still in flight,
    // and dropped once that row's current save settles so the next toggle reads `properties` again.
    const pendingTagsRef = useRef(new Map());
    // Last tagsAssigned array a PATCH response actually confirmed for a row, written unconditionally
    // on every settled response -- tags/README.md, "confirmedTagsRef" for why this is not simply
    // pin's confirmedPinRef pattern ported over (f-8 round 2, finding 1).
    const confirmedTagsRef = useRef(new Map());

    const nextSaveGeneration = (key) => {
        const gen = (saveGenerationsRef.current.get(key) ?? 0) + 1;
        saveGenerationsRef.current.set(key, gen);
        return gen;
    };

    const isCurrentSave = (key, gen) => saveGenerationsRef.current.get(key) === gen;

    // Same 401/403 carve-out as Arv.jsx/PropertyDashboard.jsx: the shared axios instance already
    // redirects on a failed refresh, and ExpiredModal already covers the screen on a subscription
    // lapse, so neither is "failing" in the sense this banner exists for.
    const markSaveFailed = (key, err, gen) => {
        // Fail-closed, ahead of the generation check: `stage:` never passes a `gen` (see
        // saveGenerationsRef's own comment above), so `gen !== undefined && ...` can never gate an
        // orphaned settlement for it on its own -- a row's own deletion tombstones every key derived
        // from its id here regardless (f-8 round 2, finding 2).
        if (deletedKeysRef.current.has(key)) return;
        if (gen !== undefined && !isCurrentSave(key, gen)) return;
        if (err?.response?.status === 401) return;
        const code = err?.response?.data?.error?.code;
        if (err?.response?.status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) return;
        failedSavesRef.current.add(key);
        setSaveError("We couldn't save your changes. Please try again.");
    };

    const markSaveOk = (key, gen) => {
        // Same tombstone as markSaveFailed above -- defensive/redundant with handleDelete's own
        // cleanup, which already removes this key before any settlement can reach here. Kept for
        // consistency across both functions, not because a reachable scenario depends on it here.
        if (deletedKeysRef.current.has(key)) return;
        if (gen !== undefined && !isCurrentSave(key, gen)) return;
        failedSavesRef.current.delete(key);
        if (failedSavesRef.current.size === 0) setSaveError(null);
    };

    const load = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        // Cleared on every reload, same as Arv.jsx's confirmedArchivedRef on a new search -- a row's
        // rollback target must not survive past a fetch that may return a different value for it, or
        // a row the fresh list no longer contains at all.
        confirmedPinRef.current = new Map();
        pendingTagsRef.current = new Map();
        confirmedTagsRef.current = new Map();
        deletedKeysRef.current = new Set();
        let clearedAnyPinFailure = false;
        for (const key of Array.from(failedSavesRef.current)) {
            if (key.startsWith('pin:')) {
                failedSavesRef.current.delete(key);
                clearedAnyPinFailure = true;
            }
        }
        if (clearedAnyPinFailure && failedSavesRef.current.size === 0) setSaveError(null);
        try {
            // Tags are supplementary — a tags failure must not blank the grid.
            const [propertiesRes, tagsRes] = await Promise.allSettled([
                reilmAPI.getProperties(),
                reilmAPI.getTags(),
            ]);
            if (propertiesRes.status === 'rejected') throw propertiesRes.reason;

            const list = Array.isArray(propertiesRes.value.data?.data?.properties)
                ? propertiesRes.value.data.data.properties
                : [];
            const tags = tagsRes.status === 'fulfilled' && Array.isArray(tagsRes.value.data?.data?.tags)
                ? tagsRes.value.data.data.tags
                : [];
            setProperties(list);
            setCustomTags(tags);
        } catch (err) {
            setFetchError(toFetchError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const allTags = useMemo(() => [
        ...SYSTEM_TAGS,
        ...customTags.map(t => ({ value: t._id, label: t.name, color: t.color })),
    ], [customTags]);

    const tagCounts = useMemo(() => {
        const counts = {};
        properties.forEach(p => {
            (p.tagsAssigned ?? []).forEach(v => {
                counts[v] = (counts[v] ?? 0) + 1;
            });
        });
        return counts;
    }, [properties]);

    const mapPropertyToGridRow = (property) => {
        const beds = property.details?.beds ?? "--";
        const baths = property.details?.baths ?? "--";
        // SOW 4: sqft as x,xxx ("1,850 sqft"), same guard as ArvMap.jsx.
        const rawSqft = property.details?.sqft;
        const sqft = rawSqft != null && rawSqft !== '' && Number.isFinite(Number(rawSqft))
            ? Number(rawSqft).toLocaleString('en-US')
            : "--";
        const yearBuilt = property.details?.yearBuilt ?? "--";
        // Comment Set #1, Pipeline 1 and ARV 7.7. Row 1 of the Property column is two runs, not one
        // string: the street bold and the city, state and zip regular beside it. They arrive as two
        // fields because the column has to style them separately, and because a single joined string
        // is exactly what stopped the weight from landing here in the first place.
        //
        // The joining is the export's, not ours: `10425 Old Mill Creek Rd Denver, CO 80202` — a
        // space after the street, a comma after the city, a space before the zip. `addressParts
        // .join(", ")` gave `..., Decatur, GA, 30032`, a comma between state and zip that is drawn
        // as a space.
        const street = property.address?.street ?? "";
        const cityStateZip = [
            property.address?.city,
            [property.address?.state, property.address?.zip].filter(Boolean).join(" "),
        ].filter(Boolean).join(", ");

        return {
            id: property._id,
            streetDisplay: street,
            cityStateZip,
            propertySummary: `${beds} bd • ${baths} ba • ${sqft} sqft • ${yearBuilt}`,
            score: 0,
            // The identifier under one name, matching the backend field (modules/properties/model.js
            // `stage`) — StageCellEditor reads this to preselect the current option. A second field
            // here holding the resolved tab-group key (via getStageFromStatus) used to sit alongside
            // it under the same "stage" name and was never read by anything downstream; that
            // write-only duplicate is gone rather than fixed.
            stage: property.stage,
            // Locale pinned to en-US with explicit digit widths: an unlocaled toLocaleDateString()
            // follows the browser (M/D/YYYY on en-US, D/M/YYYY on en-GB), and the spec's Added
            // value (pipeline.md §1) is "8/17/26" — no leading zeros, two-digit year.
            createdAtDisplay: new Date(property.createdAt).toLocaleDateString('en-US', {
                year: '2-digit',
                month: 'numeric',
                day: 'numeric',
            }),
            tagsAssigned: property.tagsAssigned ?? [],
            tagsDisplay: property.tagsAssigned ?? [],
            pinned: property.pin || false,
            raw: property,
        };
    }

    const filteredProperties = useMemo(() => {
        if (activeStage === 'all') return properties;
        return properties.filter(p => getStageFromStatus(p.stage) === activeStage);
    }, [properties, activeStage]);

    // A tab change clears the selection, so Delete never removes rows the new tab hides, and goes
    // back to page 1 (SOW 4 A1, spec.md d3/e).
    const handleTabSelect = (newStage) => {
        if (newStage === activeStage) return;
        setActiveStage(newStage);
        setSelectedRows([]);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }

    const handlePageChange = (page) => {
        scrollAfterPageRef.current = true;
        setPaginationModel(prev => ({ ...prev, page }));
    }

    const handlePageSizeChange = (pageSize) => {
        scrollAfterPageRef.current = true;
        setPaginationModel({ page: 0, pageSize });
    }

    // Spec d3: after a page change the table top is in view. Measured only after the new rows have
    // committed and laid out (rAF), since the page height changes with them; scrolling earlier left
    // the user at the end of the new page (browser QA 18.10).
    useEffect(() => {
        if (!scrollAfterPageRef.current) return undefined;
        scrollAfterPageRef.current = false;
        const frame = requestAnimationFrame(() => {
            const top = gridTopRef.current?.getBoundingClientRect().top;
            if (top < 0) window.scrollTo({ top: window.scrollY + top - 24 });
        });
        return () => cancelAnimationFrame(frame);
    }, [paginationModel.page, paginationModel.pageSize]);

    // getStageCounts (utils.js) is the same computation this used to duplicate inline, with its own
    // hardcoded acquisitions/projects/dispositions keys — a third copy of the group-key list next to
    // options.js and PipelineTabs.jsx. One function now backs both the tab counts and (previously)
    // this block.
    const stageCounts = useMemo(() => getStageCounts(properties), [properties]);

    const handleConfirmModal = () => {
        setDeleteError(null);
        setShowConfirmModal(prev => !prev);
    }

    const handleDelete = async () => {
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            if (selectedRows.length === 1) {
                await reilmAPI.deleteProperty(selectedRows[0]);
            } else {
                await reilmAPI.deletePropertiesBatch({ ids: selectedRows });
            }
            setProperties(prev => prev.filter(p => !selectedRows.includes(p._id)));
            // A hard-deleted row's save state can never settle again -- without this, an entry a
            // still-open banner depends on has nothing left to clear it (f-8, finding 3).
            let clearedAnyFailure = false;
            selectedRows.forEach((propertyId) => {
                ['pin:', 'stage:', 'strategy:', 'tags:'].forEach((prefix) => {
                    const key = `${prefix}${propertyId}`;
                    if (failedSavesRef.current.delete(key)) clearedAnyFailure = true;
                    saveGenerationsRef.current.delete(key);
                    // Tombstoned regardless of prefix -- markSaveOk/markSaveFailed's fail-closed
                    // check (f-8 round 2, finding 2); `stage:` and `strategy:` never pass a
                    // generation, which is what needs it.
                    deletedKeysRef.current.add(key);
                });
                confirmedPinRef.current.delete(propertyId);
                pendingTagsRef.current.delete(propertyId);
                confirmedTagsRef.current.delete(propertyId);
            });
            if (clearedAnyFailure && failedSavesRef.current.size === 0) setSaveError(null);
            setSelectedRows([]);
            setShowConfirmModal(false);
        } catch (err) {
            setDeleteError(err?.response?.data?.error?.message || 'Failed to delete. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    }

    const handleStageUpdate = (propertyId, newStage) => {
        setProperties(prev =>
            prev.map(p => p._id === propertyId ? { ...p, stage: newStage } : p)
        );
    };

    const handleStrategyUpdate = (propertyId, strategy) => {
        setProperties(prev =>
            prev.map(p => p._id === propertyId ? { ...p, strategy } : p)
        );
    };

    const handleTagsUpdate = (propertyId, tagsAssigned) => {
        setProperties(prev =>
            prev.map(p => p._id === propertyId ? { ...p, tagsAssigned } : p)
        );
    };

    const handlePinUpdate = (propertyId, pinValue) => {
        setProperties(prev =>
            prev.map(p => p._id === propertyId ? { ...p, pin: pinValue } : p)
        );
    };

    // Save lives here, not in columns.js's button, so it sits beside `properties`, the confirmed-value
    // ref and the generation counter that gate it -- same discipline as PropertyDashboard.jsx's own
    // pin field (src/pages/README.md), keyed per row.
    const handlePinToggle = (propertyId, next) => {
        const key = `pin:${propertyId}`;
        const gen = nextSaveGeneration(key);
        if (!confirmedPinRef.current.has(propertyId)) {
            confirmedPinRef.current.set(propertyId, !next);
        }
        handlePinUpdate(propertyId, next);
        reilmAPI.updatedPropertiesDetails(propertyId, { pin: next })
            .then((res) => {
                // Unconditional, even for a stale settlement -- src/pages/README.md's
                // confirmedSavesRef note: completion order, not issue order, is the best available
                // estimate of what the server actually holds. Reads the response body (f-9), not the
                // value sent -- `?? next` only covers a response shape that omits the path, and `??`
                // rather than `||` because `pin: false` is a legitimate saved value.
                const saved = getIn(res?.data?.data?.property, 'pin');
                confirmedPinRef.current.set(propertyId, saved ?? next);
                markSaveOk(key, gen);
            })
            .catch((err) => {
                // Rollback gated on being the current generation for this row -- a stale failure's
                // rollback predates whatever a newer toggle on this same row has already done.
                // `.has()` fallback to `!next` mirrors Arv.jsx's own confirmedArchivedRef read --
                // currently unreachable here (load() only runs once per mount) but cheap parity.
                if (isCurrentSave(key, gen)) {
                    handlePinUpdate(
                        propertyId,
                        confirmedPinRef.current.has(propertyId) ? confirmedPinRef.current.get(propertyId) : !next
                    );
                }
                markSaveFailed(key, err, gen);
            });
    };

    // Row-level discipline, matching pin: tagsAssigned is one whole-array field, so the unit that
    // needs a generation and a build base is the ROW, not the individual tag being toggled (f-8,
    // findings 1-3 and 7 -- tags/README.md carries the fuller reasoning). TagCellEditor calls this
    // and owns only the per-tag disabled-button UI around the promise it returns.
    const handleTagToggle = (propertyId, tagValue, currentAssigned) => {
        const key = `tags:${propertyId}`;
        const base = pendingTagsRef.current.has(propertyId)
            ? pendingTagsRef.current.get(propertyId)
            : currentAssigned;
        const next = base.includes(tagValue) ? base.filter(v => v !== tagValue) : [...base, tagValue];
        pendingTagsRef.current.set(propertyId, next);
        const gen = nextSaveGeneration(key);
        return reilmAPI.updatedPropertiesDetails(propertyId, { tagsAssigned: next })
            .then((res) => {
                // Unconditional, even for a stale settlement -- confirmedTagsRef's own README section
                // covers why this reads `next` (what THIS request sent), not a live pendingTagsRef
                // read: a live read here could belong to a newer, still-building toggle that has
                // nothing to do with what this specific response is confirming (f-8 round 2, finding 1).
                const saved = getIn(res?.data?.data?.property, 'tagsAssigned');
                confirmedTagsRef.current.set(propertyId, Array.isArray(saved) ? saved : next);
                if (isCurrentSave(key, gen)) {
                    // Reads pendingTagsRef live, not the `next` captured above -- the only thing that
                    // could have mutated it since is handleTagDeleted's own correction. Full reasoning:
                    // tags/README.md, "The external-change corner case".
                    const applied = pendingTagsRef.current.has(propertyId)
                        ? pendingTagsRef.current.get(propertyId)
                        : next;
                    handleTagsUpdate(propertyId, applied);
                    pendingTagsRef.current.delete(propertyId);
                } else if (failedSavesRef.current.has(key)) {
                    // This gated-out success may be the only evidence the client has of the server's
                    // real state, for a row whose current generation already failed -- see
                    // tags/README.md, "confirmedTagsRef", for the ordering this closes (f-8 round 2,
                    // finding 1).
                    handleTagsUpdate(propertyId, confirmedTagsRef.current.get(propertyId));
                }
                markSaveOk(key, gen);
            })
            .catch((err) => {
                // Current-gen failure leaves `properties` untouched -- it is already correct (only a
                // current-generation success writes it), and resyncing from confirmedTagsRef here can
                // resurface a value it has since moved past. Known gap: tags/README.md, "confirmedTagsRef".
                if (isCurrentSave(key, gen)) {
                    pendingTagsRef.current.delete(propertyId);
                }
                markSaveFailed(key, err, gen);
            });
    };

    const handleTagCreated = (tag) => {
        setCustomTags(prev => [...prev, tag]);
    };

    const handleTagUpdated = (tag) => {
        setCustomTags(prev => prev.map(t => t._id === tag._id ? tag : t));
    };

    const handleTagDeleted = (tagId) => {
        setCustomTags(prev => prev.filter(t => t._id !== tagId));
        setProperties(prev =>
            prev.map(p => ({
                ...p,
                tagsAssigned: (p.tagsAssigned ?? []).filter(v => v !== tagId),
            }))
        );
        // A row with a tag save in flight keeps its own build in pendingTagsRef, not in `properties`
        // -- the setProperties correction above never reaches it. Without this, the next toggle on
        // that row would build its PATCH body from a base that still names an id no Tag document
        // exists for any more (f-8, finding 1).
        for (const [propertyId, tags] of pendingTagsRef.current) {
            if (tags.includes(tagId)) {
                pendingTagsRef.current.set(propertyId, tags.filter(v => v !== tagId));
            }
        }
    };

    const columns = useMemo(
        () => getPipelineColumns({
            activeStage,
            onStageUpdate: handleStageUpdate,
            onStrategyUpdate: handleStrategyUpdate,
            allTags,
            onToggleTag: handleTagToggle,
            onManageTags: () => setShowManageTags(true),
            onNavigate: (id) => navigate(`/property/${id}`),
            onPinToggle: handlePinToggle,
            onSaveOk: markSaveOk,
            onSaveFailed: markSaveFailed,
            user,
        }),
        [activeStage, allTags, user] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const rows = [...filteredProperties]
        .sort((a, b) => (b.pin ? 1 : 0) - (a.pin ? 1 : 0))
        .map(mapPropertyToGridRow);

    // After a delete the stored page can point past the end; clamp to the last existing page.
    const pageCount = Math.max(1, Math.ceil(rows.length / paginationModel.pageSize));
    const effectivePagination = paginationModel.page > pageCount - 1
        ? { ...paginationModel, page: pageCount - 1 }
        : paginationModel;

    const selectedCount = selectedRows.length;
    const deleteTitle = selectedCount === 1 ? 'Delete 1 Property?' : `Delete ${selectedCount} Properties?`;
    const deleteBody = selectedCount === 1
        ? <>This property will be permanently deleted.<br />This action cannot be undone.</>
        : <>These properties will be permanently deleted.<br />This action cannot be undone.</>;

    return (
        <PageShell>
            <ConfirmModal
                open={showConfirmModal}
                onClose={handleConfirmModal}
                onConfirm={handleDelete}
                isLoading={deleteLoading}
                error={deleteError}
                title={deleteTitle}
                body={deleteBody}
                confirmLabel="Delete"
                width={360}
                panelBg="#F8FEF4"
                frame={DELETE_PROPERTIES_FRAME}
            />
            <ManageTagsModal
                open={showManageTags}
                onClose={() => setShowManageTags(false)}
                customTags={customTags}
                tagCounts={tagCounts}
                onTagCreated={handleTagCreated}
                onTagUpdated={handleTagUpdated}
                onTagDeleted={handleTagDeleted}
            />
            <AppNavbar />
            {/* From 1232 (screens.canvas) the table is the frame's full 1200; below, the old gutters.
                overflow-anchor none: scroll anchoring otherwise pins the pagination bar on screen
                while a page of a different height renders, pulling the window away from the table. */}
            <div className="px-4 md:px-6 canvas:px-0 pb-[32px] [overflow-anchor:none]">
            <section className="mt-[24px] mb-[8px]" >
            <div className='flex flex-wrap items-center justify-between gap-3 pb-4'>
                <span className='flex gap-x-[8px] max-w-full'>
                    <AddOpportunity expanded={addExpanded} onExpandedChange={setAddExpanded} />
                    {/* SOW 4: "The expanded Add Property hides the Delete button". */}
                    {/* SOW 4 A1c, frame `states` btn_12-14: disabled, enabled, hover (focus-visible = hover). */}
                    {!addExpanded && <button
                        type="button"
                        className={`group flex h-[40px] shrink-0 items-center gap-x-[6px] rounded-[20px] border border-brand-fieldBdr bg-white px-[16px] text-[13px] font-semibold transition-colors outline-none ${
                            selectedCount > 0
                                ? 'text-brand-dark hover:border-[#D85263] focus-visible:border-[#D85263]'
                                : 'text-brand-placeholder cursor-not-allowed'
                        }`}
                        onClick={selectedCount > 0 ? handleConfirmModal : undefined}
                        disabled={selectedCount === 0}
                    >
                        {selectedCount > 0
                            ? <DeleteIcon outline="#4F5F4A" accentClassName="group-hover:stroke-[#D85263] group-focus-visible:stroke-[#D85263]" />
                            : <DeleteIcon outline="#8A9A85" />}
                        Delete
                    </button>}
                </span>
                <div className="max-w-full overflow-x-auto">
                    <PipelineTabs
                        activeStage={activeStage}
                        onStageChange={handleTabSelect}
                        stageCounts={stageCounts}
                    />
                </div>
            </div>
            </section>
            {loading && (
                <div className="flex items-center justify-center py-20 text-brand-secondary text-[20px]">
                    Loading…
                </div>
            )}
            {fetchError && (
                <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                    <p className="text-brand-dark text-[20px] font-semibold">{fetchError.title}</p>
                    <p className="text-brand-secondary text-[15px]">{fetchError.body}</p>
                    <button
                        type="button"
                        onClick={fetchError.locked ? () => navigate('/pricing') : load}
                        className="mt-2 h-[44px] px-6 rounded-full bg-brand-green text-white text-[14px] font-semibold hover:opacity-90 transition-opacity"
                    >
                        {fetchError.locked ? 'View Plans' : 'Try Again'}
                    </button>
                </div>
            )}
            {!loading && !fetchError && (
                <>
                    {/* Additive, not a replacement for the grid -- a failed row-level save must not
                        take the rest of the table down with it, unlike fetchError above. */}
                    {saveError && (
                        <p className="text-red-400 text-[14px] mb-[8px]">{saveError}</p>
                    )}
                    <div ref={gridTopRef} data-pipeline-grid-top>
                        <PipelineGrid
                            rows={rows}
                            columns={columns}
                            selectedRows={selectedRows}
                            onRowSelectionChange={setSelectedRows}
                            paginationModel={effectivePagination}
                            onPaginationModelChange={setPaginationModel}
                        />
                    </div>
                    {rows.length > 0 && (
                        <div className="mt-[24px]">
                            <PipelinePagination
                                page={effectivePagination.page}
                                pageSize={effectivePagination.pageSize}
                                rowCount={rows.length}
                                pageSizeOptions={PIPELINE_PAGE_SIZES}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        </div>
                    )}
                </>
            )}
            </div>
        </PageShell>
    )
}

export default Pipeline
